/**
 * Borrowing Calculator
 * 대출 가능 금액 계산기 - 호주 부동산
 */

import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Calculator, DollarSign, ArrowLeft, Info } from "lucide-react";
import { useState, useEffect } from "react";
import { useLocation } from "wouter";

export default function BorrowingCalculator() {
  const [, setLocation] = useLocation();

  // URL 파라미터에서 초기값 가져오기
  const urlParams = new URLSearchParams(window.location.search);
  const initialPropertyPrice = urlParams.get("price") ? parseInt(urlParams.get("price") || "0") / 100 : 0;

  const [annualIncome, setAnnualIncome] = useState<number>(0);
  const [partnerIncome, setPartnerIncome] = useState<number>(0);
  const [monthlyExpenses, setMonthlyExpenses] = useState<number>(0);
  const [existingDebts, setExistingDebts] = useState<number>(0); // Monthly debt repayments
  const [interestRate, setInterestRate] = useState<number>(6.5); // %
  const [loanTerm, setLoanTerm] = useState<number>(30); // years
  const [deposit, setDeposit] = useState<number>(20); // %
  const [propertyPrice, setPropertyPrice] = useState<number>(initialPropertyPrice || 0);

  // 계산 결과
  const [results, setResults] = useState<{
    maxBorrowingAmount: number;
    maxPropertyPrice: number;
    monthlyRepayment: number;
    depositRequired: number;
    lvr: number; // Loan to Value Ratio
    serviceability: boolean;
  } | null>(null);

  // 대출 가능 금액 계산 (호주 기준)
  const calculateBorrowing = () => {
    if (!annualIncome || annualIncome <= 0) {
      setResults(null);
      return;
    }

    const totalAnnualIncome = annualIncome + (partnerIncome || 0);
    const totalMonthlyIncome = totalAnnualIncome / 12;
    const netMonthlyIncome = totalMonthlyIncome - monthlyExpenses - existingDebts;

    // 호주 은행 기준: 일반적으로 월 소득의 30-35%를 대출 상환에 사용 가능
    // 보수적으로 30% 사용
    const availableForRepayment = netMonthlyIncome * 0.30;

    // 대출 상환액으로부터 대출 금액 역산
    const monthlyRate = interestRate / 100 / 12;
    const numPayments = loanTerm * 12;

    let maxLoanAmount = 0;
    if (monthlyRate === 0) {
      maxLoanAmount = availableForRepayment * numPayments;
    } else {
      maxLoanAmount = availableForRepayment *
        (Math.pow(1 + monthlyRate, numPayments) - 1) /
        (monthlyRate * Math.pow(1 + monthlyRate, numPayments));
    }

    // 최대 대출 금액 (보수적으로 5.5% 스트레스 테스트 적용)
    const stressRate = 0.055; // 5.5% 스트레스 테스트
    const stressMonthlyRate = stressRate / 12;
    const stressMaxLoan = availableForRepayment *
      (Math.pow(1 + stressMonthlyRate, numPayments) - 1) /
      (stressMonthlyRate * Math.pow(1 + stressMonthlyRate, numPayments));

    // 더 보수적인 값 사용
    const finalMaxLoan = Math.min(maxLoanAmount, stressMaxLoan);

    // 최대 매물 가격 계산
    const depositPercent = deposit / 100;
    const maxPropertyPrice = finalMaxLoan / (1 - depositPercent);
    const depositRequired = maxPropertyPrice * depositPercent;

    // 선택한 매물 가격에 대한 계산
    let monthlyRepayment = 0;
    let lvr = 0;
    if (propertyPrice > 0) {
      const loanAmount = propertyPrice * (1 - depositPercent);
      lvr = (loanAmount / propertyPrice) * 100;

      if (monthlyRate === 0) {
        monthlyRepayment = loanAmount / numPayments;
      } else {
        monthlyRepayment = loanAmount *
          (monthlyRate * Math.pow(1 + monthlyRate, numPayments)) /
          (Math.pow(1 + monthlyRate, numPayments) - 1);
      }
    }

    // 서비스 가능 여부 (월 상환액이 가능한 금액 이하인지)
    const serviceability = propertyPrice > 0 ? monthlyRepayment <= availableForRepayment : true;

    setResults({
      maxBorrowingAmount: finalMaxLoan,
      maxPropertyPrice,
      monthlyRepayment: propertyPrice > 0 ? monthlyRepayment : 0,
      depositRequired: propertyPrice > 0 ? propertyPrice * depositPercent : depositRequired,
      lvr: propertyPrice > 0 ? lvr : (1 - depositPercent) * 100,
      serviceability,
    });
  };

  useEffect(() => {
    if (annualIncome > 0) {
      calculateBorrowing();
    }
  }, [annualIncome, partnerIncome, monthlyExpenses, existingDebts, interestRate, loanTerm, deposit, propertyPrice]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-AU", {
      style: "currency",
      currency: "AUD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <PageContainer size="default">
      <div className="max-w-4xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => setLocation("/properties")}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          목록으로
        </Button>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calculator className="w-5 h-5" />
              Borrowing Calculator
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-2">
              Calculate how much you can borrow based on your income and expenses
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* 소득 정보 */}
            <div>
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <DollarSign className="w-4 h-4" />
                Income Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="annual-income">Your Annual Income (AUD)</Label>
                  <Input
                    id="annual-income"
                    type="number"
                    value={annualIncome || ""}
                    onChange={(e) => setAnnualIncome(parseFloat(e.target.value) || 0)}
                    placeholder="80000"
                  />
                </div>
                <div>
                  <Label htmlFor="partner-income">Partner Annual Income (AUD)</Label>
                  <Input
                    id="partner-income"
                    type="number"
                    value={partnerIncome || ""}
                    onChange={(e) => setPartnerIncome(parseFloat(e.target.value) || 0)}
                    placeholder="0"
                  />
                </div>
              </div>
            </div>

            <Separator />

            {/* 지출 정보 */}
            <div>
              <h3 className="font-semibold mb-4">Expenses</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="monthly-expenses">Monthly Living Expenses (AUD)</Label>
                  <Input
                    id="monthly-expenses"
                    type="number"
                    value={monthlyExpenses || ""}
                    onChange={(e) => setMonthlyExpenses(parseFloat(e.target.value) || 0)}
                    placeholder="3000"
                  />
                </div>
                <div>
                  <Label htmlFor="existing-debts">Existing Monthly Debt Repayments (AUD)</Label>
                  <Input
                    id="existing-debts"
                    type="number"
                    value={existingDebts || ""}
                    onChange={(e) => setExistingDebts(parseFloat(e.target.value) || 0)}
                    placeholder="500"
                  />
                </div>
              </div>
            </div>

            <Separator />

            {/* 대출 정보 */}
            <div>
              <h3 className="font-semibold mb-4">Loan Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="interest-rate">Interest Rate (%)</Label>
                  <Input
                    id="interest-rate"
                    type="number"
                    step="0.1"
                    value={interestRate}
                    onChange={(e) => setInterestRate(parseFloat(e.target.value) || 0)}
                  />
                </div>
                <div>
                  <Label htmlFor="loan-term">Loan Term (years)</Label>
                  <Input
                    id="loan-term"
                    type="number"
                    value={loanTerm}
                    onChange={(e) => setLoanTerm(parseInt(e.target.value) || 30)}
                  />
                </div>
                <div>
                  <Label htmlFor="deposit">Deposit (%)</Label>
                  <Input
                    id="deposit"
                    type="number"
                    step="0.1"
                    value={deposit}
                    onChange={(e) => setDeposit(parseFloat(e.target.value) || 0)}
                  />
                </div>
                <div>
                  <Label htmlFor="property-price">Property Price (AUD) - Optional</Label>
                  <Input
                    id="property-price"
                    type="number"
                    value={propertyPrice || ""}
                    onChange={(e) => setPropertyPrice(parseFloat(e.target.value) || 0)}
                    placeholder="800000"
                  />
                </div>
              </div>
            </div>

            <Separator />

            {/* 결과 */}
            {results && (
              <div className="space-y-4">
                <h3 className="font-semibold text-lg">Results</h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Maximum Borrowing</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-sm text-muted-foreground">Max Loan Amount:</span>
                          <span className="font-bold text-lg">{formatCurrency(results.maxBorrowingAmount)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-muted-foreground">Max Property Price:</span>
                          <span className="font-semibold">{formatCurrency(results.maxPropertyPrice)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-muted-foreground">Deposit Required:</span>
                          <span className="font-semibold">{formatCurrency(results.depositRequired)}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {propertyPrice > 0 && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-base">Property Analysis</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-sm text-muted-foreground">Monthly Repayment:</span>
                            <span className="font-semibold">{formatCurrency(results.monthlyRepayment)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-muted-foreground">Loan to Value Ratio:</span>
                            <span className="font-semibold">{results.lvr.toFixed(1)}%</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-muted-foreground">Serviceability:</span>
                            <span className={`font-semibold ${results.serviceability ? "text-green-600" : "text-red-600"}`}>
                              {results.serviceability ? "✓ Affordable" : "✗ May not qualify"}
                            </span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>

                {propertyPrice > 0 && !results.serviceability && (
                  <Card className="bg-yellow-50 dark:bg-yellow-950 border-yellow-200">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-2">
                        <Info className="w-5 h-5 text-yellow-600 mt-0.5" />
                        <div>
                          <p className="text-sm text-yellow-900 dark:text-yellow-100">
                            This property may be outside your borrowing capacity.
                            Consider increasing your deposit or looking at properties up to {formatCurrency(results.maxPropertyPrice)}.
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                <div className="p-4 bg-muted rounded-lg">
                  <p className="text-xs text-muted-foreground">
                    <strong>Note:</strong> This calculator provides an estimate only.
                    Actual borrowing capacity depends on various factors including credit history,
                    employment stability, and lender policies. Australian banks typically use a
                    "serviceability buffer" (currently around 3%) above the actual interest rate
                    to assess loan applications. Please consult with a mortgage broker or lender
                    for accurate borrowing capacity assessment.
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </PageContainer>
  );
}

